import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import { useIsAuthenticated } from '@azure/msal-react';
import { useQueryClient } from '@tanstack/react-query';

import { useConsulteePermission, API_QUERY_KEYS } from 'api';
import { useNavigate, useNavigation } from 'hooks';
import {
  hasPermissionState,
  isAdminOrConsulteeState,
  isTQAdminState,
  isAdminState,
  isConsulteeState,
} from 'store';

type usePermissionsReturn = {
  hasPageViewPermissions: boolean;
  isFetchingPermissions: boolean;
};

export const usePermissions = (): usePermissionsReturn => {
  const location = useLocation();
  const isAuthenticated = useIsAuthenticated();
  const queryClient = useQueryClient();

  const hasPermission = useRecoilValue(hasPermissionState);
  const isTQAdmin = useRecoilValue(isTQAdminState);
  const isAdmin = useRecoilValue(isAdminState);
  const isConsultee = useRecoilValue(isConsulteeState);
  const isAdminOrConsultee = useRecoilValue(isAdminOrConsulteeState);

  const { navigateToDashboard, navigateToTrafficMonitor } = useNavigate();

  const { isLoading } = useConsulteePermission(isAuthenticated);

  const { isHomePage, isMonitorPage, isManageTeamPage, isCreateOrganisationPage } =
    useNavigation(location.pathname);

  const [hasPageViewPermissions, setHasPageViewPermissions] = useState<boolean>(false);
  const [isFetchingPermissions, setIsFetchingPermissions] = useState<boolean>(true);

  useEffect(() => {
    if (isAuthenticated && !hasPermission)
      queryClient.invalidateQueries({ queryKey: [API_QUERY_KEYS.getConsulteePermission] });
  }, [isAuthenticated, hasPermission, queryClient]);

  useEffect(() => {
    if (isHomePage && hasPermission) {
      if (isTQAdmin) navigateToTrafficMonitor();
      else if (isAdminOrConsultee) navigateToDashboard();
    }
  }, [
    hasPermission,
    isHomePage,
    isTQAdmin,
    isAdminOrConsultee,
    navigateToDashboard,
    navigateToTrafficMonitor,
  ]);

  useEffect(() => {
    const tqAdminPermissionRequired = isMonitorPage || isCreateOrganisationPage;
    const adminPermissionRequired = isManageTeamPage;

    if (isTQAdmin) setHasPageViewPermissions(true);
    else if (isAdmin) setHasPageViewPermissions(!tqAdminPermissionRequired);
    else if (isConsultee)
      setHasPageViewPermissions(!tqAdminPermissionRequired && !adminPermissionRequired);
    else setHasPageViewPermissions(isHomePage && !isAuthenticated);
  }, [
    isMonitorPage,
    isManageTeamPage,
    isTQAdmin,
    isAdmin,
    isConsultee,
    isHomePage,
    isAuthenticated,
    isCreateOrganisationPage,
  ]);

  useEffect(() => {
    setIsFetchingPermissions(
      isLoading || (!hasPermission && isAuthenticated) || (hasPermission && isHomePage)
    );
  }, [isLoading, hasPermission, isAuthenticated, isHomePage]);

  return {
    hasPageViewPermissions,
    isFetchingPermissions,
  };
};
